/**
 * @deprecated
 */
import { Property } from 'csstype';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { TableCell, TableHeader } from '../table';
import { SmartTableCell } from './smart-table-cell/SmartTableCell';
import * as S from './SmartTable.styles';
import { SmartTableCellProps, SmartTableDataItem } from './SmartTable.types';
import {
  TableColumnsEditor,
  TableColumnsEditorTranslations,
} from './table-columns-editor/TableColumnsEditor';

export type StringKeys<T> = Extract<keyof T, string>;

export type SmartTableColumn<T> = {
  [ColumnKey in StringKeys<T>]: {
    key: ColumnKey;
    label: string;
    alignment?: Property.TextAlign;
    disableHiding?: boolean;
    hiddenByDefault?: boolean;
    sticky?: boolean;
    render?: (value: T[ColumnKey], item: T) => ReactNode;
    renderHeader?: () => ReactNode;
    renderCell?: (props: SmartTableCellProps<T>) => ReactNode;
  };
}[StringKeys<T>];

export interface SmartTableProps<T> {
  className?: string;
  withBodyRowHover?: boolean;
  isHeaderSticky?: boolean;
  editableColumns?: boolean;
  columns: SmartTableColumn<T>[];
  data: SmartTableDataItem<T>[];
  headerTitle?: { title: string; colSpan?: number }[];
  alignment?: Property.TextAlign;
  i18nTranslations?: {
    editColumnTooltip: string;
  };
  onActiveColumnsChange?: (newColumns: StringKeys<T>[]) => void;
  renderActionColumn?: (item: SmartTableDataItem<T>) => ReactNode;
  onRowClick?: (item: SmartTableDataItem<T>) => void;
  activeColumnChangeHandler?: (
    activeColumnKeys: StringKeys<T>[],
    name: StringKeys<T>,
    isChecked: boolean
  ) => StringKeys<T>[];
  useTableScroll?: boolean;
}

const getColumnKeys = <T,>(columns: SmartTableProps<T>['columns']) =>
  columns.filter(c => !c.hiddenByDefault).map(c => c.key);

export const SmartTable = <T extends {}>({
  withBodyRowHover = true,
  className,
  isHeaderSticky = false,
  editableColumns = false,
  columns,
  data,
  headerTitle,
  alignment = 'left',
  i18nTranslations,
  onActiveColumnsChange,
  onRowClick,
  renderActionColumn,
  activeColumnChangeHandler,
  useTableScroll = false,
}: SmartTableProps<T>) => {
  const ref = useRef<HTMLTableElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const columnKeys = getColumnKeys(columns);
  const [activeColumnKeys, setActiveColumnKeys] = useState(columnKeys);
  const [firstColumnWidth, setFirstColumnWidth] = useState<number | null>(null);

  useEffect(() => {
    setActiveColumnKeys(getColumnKeys(columns));
  }, [columnKeys.toString()]);

  const handleActiveColumnsChange = (
    name: StringKeys<T>,
    isChecked: boolean
  ) => {
    let keys: StringKeys<T>[];

    if (activeColumnChangeHandler) {
      const stringKeys = activeColumnChangeHandler(
        activeColumnKeys,
        name,
        isChecked
      );
      keys = stringKeys;
    } else {
      keys = isChecked
        ? [...activeColumnKeys, name]
        : activeColumnKeys.filter(key => key !== name);
    }

    setActiveColumnKeys(keys);
    onActiveColumnsChange?.(keys);
  };

  useEffect(() => {
    if (ref.current) {
      const firstColumn = ref.current.querySelector('th:first-child');

      if (firstColumn) {
        const columnWidth = firstColumn.getBoundingClientRect().width;
        setFirstColumnWidth(columnWidth);
      }
    }
  }, []);

  useEffect(() => {
    const table = ref.current as unknown as HTMLTableElement;
    const isScrollable =
      table &&
      table!.parentElement!.clientWidth < table!.parentElement!.scrollWidth;
    setHasScrollbar(isScrollable);
  }, [activeColumnKeys]);

  const activeColumns = columns.filter(column =>
    activeColumnKeys.includes(column.key)
  );
  const isFirstColumnSticky = useMemo(() => {
    return !!columns[0].sticky;
  }, [columns]);

  const tableHead = activeColumns.map(column => {
    const content = column.renderHeader ? column.renderHeader() : column.label;
    const columnAlignment = column.alignment || alignment;
    return column.sticky ? (
      <S.StickyTableHeader
        alignment={columnAlignment}
        key={column.key}
        $displayBorder={hasScrollbar}
        isHoverEnabled={editableColumns}
      >
        {content}
      </S.StickyTableHeader>
    ) : (
      <TableHeader
        key={column.key}
        alignment={columnAlignment}
        isHoverEnabled={editableColumns}
      >
        {content}
      </TableHeader>
    );
  });

  const renderContent = (content: ReactNode) =>
    useTableScroll ? (
      <S.SmartTableScrollRoot
        $isFirstColumnSticky={isFirstColumnSticky}
        $firstColumnWidth={firstColumnWidth}
      >
        {content}
      </S.SmartTableScrollRoot>
    ) : (
      <>{content}</>
    );

  return renderContent(
    <S.Table
      className={className}
      $withBodyRowHover={withBodyRowHover}
      $isHeaderSticky={isHeaderSticky}
      ref={ref}
    >
      <thead>
        {headerTitle && (
          <tr>
            {headerTitle.map(header => (
              <S.TableHeaderTitle
                key={header.title}
                colSpan={header.colSpan || columns.length}
              >
                {header.title}
              </S.TableHeaderTitle>
            ))}
          </tr>
        )}
        <tr>
          {tableHead}

          {editableColumns && (
            <TableColumnsEditor<T>
              columns={columns}
              activeColumnKeys={activeColumnKeys}
              onActiveColumnToggle={handleActiveColumnsChange}
              i18nTranslations={
                i18nTranslations as TableColumnsEditorTranslations
              }
            />
          )}
          {renderActionColumn && <TableHeader />}
        </tr>
      </thead>
      <tbody>
        {data.map(item => (
          <S.StyledTableRow
            data-testid="table-data-row"
            onClick={onRowClick && (() => onRowClick(item))}
            key={item.key}
          >
            {activeColumns.map(column => {
              const columnAlignment = column.alignment || alignment;
              return (
                column.renderCell?.({
                  item,
                  column,
                  hasScrollbar,
                  alignment: columnAlignment,
                }) || (
                  <SmartTableCell
                    key={column.key}
                    item={item}
                    column={column}
                    alignment={columnAlignment}
                    hasScrollbar={hasScrollbar}
                  />
                )
              );
            })}
            {editableColumns && (
              <TableCell key="settings" alignment={alignment} />
            )}
            {renderActionColumn && (
              <S.ActionTableCell
                key="actions"
                alignment={alignment}
                isNumber={false}
              >
                <div>{renderActionColumn(item)}</div>
              </S.ActionTableCell>
            )}
          </S.StyledTableRow>
        ))}
      </tbody>
    </S.Table>
  );
};

SmartTable.Styled = S;
SmartTable.TableCell = SmartTableCell;
